<template>

  <div id="product-list" v-resize="handleResize">
    <component :is="listComponent"
               :show-previous="true"
               :class-names="{ 'ais-InfiniteHits-loadPrevious': 'product-list__prev-btn' }">
      <template v-slot:loadPrevious="{ isFirstPage, refinePrevious }">
        <div v-if="!isFirstPage"
             ref="prevBtn"
             @click="loadPrevPage(isFirstPage, refinePrevious)"/>
      </template>
      <template v-slot="{ items, refineNext, isLastPage, sendEvent }">
        {{ updateProducts(items) }}
        <div class="wrapper">
          <products-listing-results-count v-if="mobile"
                                          name="product" />

          <div class="product-list-wrapper" v-if="items.length > 0">
            <div class="product-list" :class="getProductsDisplay" ref="productsList">
              <product-preview v-for="(product, index) in items"
                               :ref="`product-${product.product_id}`"
                               :product="product"
                               :key="`${product.product_id}-${index}`"
                               :enable-edit-ability="enableEditAbility"
                               :categories="categories"
                               :show-quick-shop-btn="quickShop2"
                               :preventDefault="quickShop1"
                               :query-id="product.__queryID"
                               :data-insights-object-id="product.objectID"
                               :data-insights-position="product.__position"
                               :data-insights-query-id="product.__queryID"
                               @productClicked="onProductClicked(product, sendEvent)"
                               @showProduct="showQuickShop(product, index, $event)"/>
              <quick-shop-1 v-if="quickShop1Visible"
                            :product="quickShop1Visible"
                            :style="selectedVariantGridPosition"
                            :query-id="items.length && items[0].__queryID"
                            @close="closeQuickShop1"/>
            </div>
          </div>
          <div class="missing-products" v-else>
            <span v-if="!productsLoading">no search results</span>
          </div>
          <div v-if="isIntiniteHits && !isLastPage"
               class="btn inverted load-more"
               :class="{ 'load-disabled': productsLoading }"
               role="button"
               @click="refineNext">
            Load More Products
          </div>
          <products-listing-pagination v-else-if="!isIntiniteHits"
                                       class="pagination" />
        </div>
      </template>
    </component>

    <products-loader :loading="productsLoading"
                     :amount="[0,1,2,3,4,5,6,7]" />
  </div>

</template>

<script>
  import ApiCategory from '@/api/admin/Category'

  import { compact, isEqual, debounce } from 'lodash'
  import { AisHits, AisInfiniteHits } from 'vue-instantsearch'
  import { mapGetters } from 'vuex'

  import GtmHelpers from '@/mixins/gtmHelpers'
  import PinterestConversionEvents from '@/mixins/pinterestConversionEvents'
  import ProductsListingResponsive from '@/mixins/products/listing/productsListingResponsive'

  import ProductPreview from '@/components/ProductPreview'
  import ProductsListingPagination from '@/components/products/listing/ProductsListingPagination'
  import ProductsListingResultsCount from '@/components/products/listing/ProductsListingResultsCount'
  import ProductsLoader from '@/components/ProductsLoader'
  import QuickShop1 from '@/components/ab_tests/QuickShop1'


  export default {
    name: 'product-list',
    components: {
      AisHits,
      AisInfiniteHits,
      ProductPreview,
      ProductsListingPagination,
      ProductsListingResultsCount,
      ProductsLoader,
      QuickShop1
    },
    mixins: [
      PinterestConversionEvents,
      ProductsListingResponsive,
      GtmHelpers
    ],
    props: {
      productsLoading: {
        type: Boolean,
        required: false,
        default: false
      },
      enableEditAbility: {
        type: Boolean,
        default: false
      }
    },
    data () {
      return {
        currentPage: this.$route.query.page ? parseInt(this.$route.query.page) : 1,
        quickShop1Visible: false,
        variantSelectionGridRow: 'none',
        variantSelectionGridColumn: 'none',
        categories: [],
        observer: null,
        quickShop1: false,
        products: [],
        scrollInterval: null,
        scrollToProductIdKey: 'scrollToProductId',
        isIntiniteHits: this.$device.isMobile || this.$device.isTablet,
        listComponent: (this.$device.isMobile || this.$device.isTablet) ? 'AisInfiniteHits' : 'AisHits'
      }
    },
    computed: {
      ...mapGetters('globalData', ['getNavigationDirection']),
      getProductsDisplay() {
        return this.$store.getters['globalData/getProductsDisplay']
      },
      selectedVariantGridPosition () {
        return {
          'grid-row': this.variantSelectionGridRow,
          'grid-column': this.variantSelectionGridColumn
        }
      },
      quickShop2 () {
        return false
        // return !this.mobile
      }
    },
    watch: {
      '$route.query.page' (page) {
        this.currentPage = page ? parseInt(page) : 1
      },
      'products' (newProducts, oldProducts) {
        let newIds = newProducts.map(e => e.objectID)
        let oldIds = oldProducts.map(e => e.objectID)
        if (isEqual(newIds, oldIds)) return
        if (!newProducts.length) return

        this.sendProductsChangedEvent(newProducts)
      }
    },
    async fetch () {
      if (this.$store.getters['auth/signedAdmin']) {
        await this.loadCategories()
      }
    },
    mounted () {
      this.setMutationObserver()
      this.scrollToProduct()
    },
    beforeDestroy () {
      this.disconnectMutationObserver()
      if (this.scrollInterval) clearInterval(this.interval)
    },
    methods: {
      sendProductsChangedEvent: debounce(function (products) {
        this.$emit('productsChanged', products)
        this.gtmViewItemList(this.$route, products.map(this.buildGtmLineItemFromAlgolia))
        this.pinterestViewCategory(this.$route.path, products)
      }, 500, { leading: true, trailing: false }),
      scrollToProduct () {
        const productId = localStorage.getItem(this.scrollToProductIdKey)
        if (!productId || this.getNavigationDirection !== 'backward') return

        this.scrollInterval = setInterval(() => {
          this.$nextTick(() => {
            let productNodes = this.$refs[`product-${productId}`]

            if (productNodes?.length > 0) {
              const productNode = productNodes[0].$el
              productNode.scrollIntoView({ behavior: 'smooth' })
              clearInterval(this.scrollInterval)
              return
            }

            this.$refs.prevBtn?.click()
          })
        }, 100)

        setTimeout(() => clearInterval(this.scrollInterval), 5000)
      },
      saveScrollToProduct ({ product_id }) {
        localStorage.setItem(this.scrollToProductIdKey, product_id)
      },
      loadPrevPage (isFirstPage, refinePrevious) {
        if (isFirstPage) return
        refinePrevious()
      },
      updateProducts (products) {
        this.products = compact(products)
      },
      loadMore() {
        if (this.currentPage < this.totalPages) {
          this.currentPage = this.currentPage + 1
          this.$router.replace({query: Object.assign({}, this.$route.query, {page: this.currentPage})})
            .then(response => {
              let newPath = response.fullPath.replace(/^\/brands\//, '/suppliers/')
              this.$emit('loadMoreProducts', newPath)
            })
        }
      },
      showQuickShop1 (product, index) {
        if (!this.quickShop1) { return }

        let grid = this.$refs.productsList
        let itemsPerRow = window.getComputedStyle(grid).getPropertyValue('grid-template-columns').split(' ').length
        let currentRow = Math.floor(index / itemsPerRow) + 1

        this.variantSelectionGridRow = `${currentRow + 1}/${currentRow + 2}`
        this.variantSelectionGridColumn = `1/${itemsPerRow + 1}`

        this.quickShop1Visible = product
      },
      showQuickShop2 (props) {
        this.$store.dispatch('modal/showModal', {
          component: 'ProductsQuickViewModal',
          classes: ['auto-width'],
          props
        })
      },
      closeQuickShop1 () {
        this.quickShop1Visible = false
      },
      loadCategories() {
        return ApiCategory.getCategoriesTree(this.$axios)
          .then(response => {
            this.categories = response.data.categories
          })
      },
      showQuickShop (product, index, props) {
        if (this.quickShop1) this.showQuickShop1(product, index)
        if (this.quickShop2) this.showQuickShop2(props)
      },
      setMutationObserver () {
        this.observer = new MutationObserver(mutations => {
          for (const mutation of mutations) {
            const newValue = mutation.target.getAttribute(mutation.attributeName)
            this.$nextTick(() => {
              switch (mutation.attributeName) {
                case 'data-quick-shop1':
                  this.quickShop1 = newValue === 'true'
                  break;
                case 'data-quick-shop2':
                  this.quickShop2 = newValue === 'true'
                  break;
              }
            })
          }
        })

        this.observer.observe(document.getElementById('product-list'), { attributes: true })
      },
      disconnectMutationObserver () {
        this.observer?.disconnect()
      },
      onProductClicked(product, sendEventToAlgolia) {
        sendEventToAlgolia('click', product, 'product_clicked')
        this.sendSelectItemEventToGtm(product)
        this.saveScrollToProduct(product)
      },
      sendSelectItemEventToGtm (product) {
        let lineItem = {
          variant: product,
          price: product.price,
          quantity: 1,
          item_brand: product.supplier.name
        }

        this.gtmSelectItem(this.$route, lineItem)
      }
    }
  }

</script>

<style lang="scss" scoped>

  ::v-deep .product-list {
    &__prev-btn {
      display: none;
    }
  }

  .wrapper{
    display: grid;
    grid-template-columns: 1fr;
    grid-row-gap: 20px;

    .product-list-wrapper {
      .product-list {
        position: relative;
        display: grid;
        row-gap: 50px;
        grid-gap: 32px;
        grid-auto-rows: min-content;

        &.col2 {
          grid-template-columns: repeat(2, 1fr);
        }

        &.col3 {
          grid-template-columns: repeat(3, 1fr);
        }

        &.col4 {
          grid-template-columns: repeat(4, 1fr);
        }

        &.list {
          grid-template-columns: repeat(1, 1fr);
        }

        @media only screen and (max-width: 992px) {
          grid-gap: 20px;

          &.col2, &.col3, &.col4, &.list {
            grid-template-columns: repeat(2, 1fr);
          }
        }
      }
    }

    .missing-products {
      font-size: 3.2rem;
      font-weight: bold;
      text-transform: capitalize;
      text-align: center;
    }

    .load-more {
      justify-self: center;
      max-width: 300px;
      margin-top: 40px;

      @media only screen and (max-width: 992px) {
        justify-self: center;
        max-width: 250px;
      }
    }

    .load-disabled {
      pointer-events: none;
      opacity: 50%;
    }

    .pagination {
      margin: 40px auto 0;
    }
  }

</style>
